/**
 * Replica - lokalna kopia danych z API w IndexedDB.
 * Odczyt gdy brak sieci lub fetch padł.
 */

import { openDb } from "./db";
import { animalsListFromReplica } from "./sanitize";
import type { ReplicaKey, ReplicaEntry } from "./types";

const REPLICA_STORE = "replica";

export async function saveToReplica(key: ReplicaKey, data: unknown): Promise<void> {
  const conn = await openDb();
  if (!conn) return;

  try {
    const entry: ReplicaEntry = {
      key,
      data,
      timestamp: Date.now(),
    };

    const tx = conn.db.transaction(REPLICA_STORE, "readwrite");
    const store = tx.objectStore(REPLICA_STORE);
    store.put(entry);

    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    conn.close();
  }
}

export async function patchReplica<T>(
  key: ReplicaKey,
  updater: (current: T | null) => T | null,
): Promise<void> {
  const current = await loadFromReplica<T>(key);
  const next = updater(current);
  if (next == null) {
    return;
  }
  await saveToReplica(key, next);
}

export async function appendToReplicaList<T>(key: ReplicaKey, item: T): Promise<void> {
  await patchReplica<T[]>(key, (current) => {
    const list = Array.isArray(current) ? current : [];
    return [...list, item];
  });
}

export async function patchAnimalsReplica<T extends { tag: string }>(
  key: "animals:active" | "animals:archived",
  updater: (list: T[]) => T[],
): Promise<void> {
  await patchReplica(key, (current) => updater(animalsListFromReplica(current) as T[]));
}

export async function loadFromReplica<T = unknown>(key: ReplicaKey): Promise<T | null> {
  const conn = await openDb();
  if (!conn) return null;

  try {
    return await new Promise((resolve, reject) => {
      const tx = conn.db.transaction(REPLICA_STORE, "readonly");
      const store = tx.objectStore(REPLICA_STORE);
      const request = store.get(key);

      request.onsuccess = () => {
        const entry = request.result as ReplicaEntry | undefined;
        resolve(entry ? (entry.data as T) : null);
      };

      request.onerror = () => {
        console.error(`[replica] Failed to load ${key}:`, request.error);
        reject(request.error);
      };
    });
  } finally {
    conn.close();
  }
}

export async function getReplicaTimestamp(key: ReplicaKey): Promise<number | null> {
  const conn = await openDb();
  if (!conn) return null;

  try {
    return await new Promise((resolve) => {
      const tx = conn.db.transaction(REPLICA_STORE, "readonly");
      const store = tx.objectStore(REPLICA_STORE);
      const request = store.get(key);

      request.onsuccess = () => {
        const entry = request.result as ReplicaEntry | undefined;
        resolve(entry?.timestamp || null);
      };

      request.onerror = () => {
        console.error(`[replica] Failed to get timestamp for ${key}:`, request.error);
        resolve(null);
      };
    });
  } finally {
    conn.close();
  }
}

export async function clearReplica(): Promise<void> {
  const conn = await openDb();
  if (!conn) return;

  try {
    const tx = conn.db.transaction(REPLICA_STORE, "readwrite");
    const store = tx.objectStore(REPLICA_STORE);
    store.clear();

    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    conn.close();
  }
}

export async function getOldestReplicaTimestamp(): Promise<number | null> {
  const conn = await openDb();
  if (!conn) return null;

  try {
    return await new Promise((resolve) => {
      const tx = conn.db.transaction(REPLICA_STORE, "readonly");
      const store = tx.objectStore(REPLICA_STORE);
      const index = store.index("timestamp");
      const request = index.openCursor(null, "next");

      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          const entry = cursor.value as ReplicaEntry;
          resolve(entry.timestamp);
        } else {
          resolve(null);
        }
      };

      request.onerror = () => {
        console.error("[replica] Failed to get oldest timestamp:", request.error);
        resolve(null);
      };
    });
  } finally {
    conn.close();
  }
}
